import { type JSX, type ReactNode, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ReferenceDot,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import { CampaignChannelChooser } from '@/components/CampaignChannelChooser';
import { Icon } from '@/components/icons';
import {
  Button,
  Card,
  DateRangePicker,
  type DateRangePreset,
  LiveDot,
  Progress,
  Skeleton,
  Sparkline,
} from '@/components/ui';
import { useAuth } from '@/hooks/useAuth';
import { useCampaigns } from '@/hooks/useCampaigns';
import { useFeature } from '@/hooks/useFeatures';
import { useStatsOverview, type StatsRange, type StatusBucket } from '@/hooks/useStats';
import { useTabVisibility } from '@/hooks/useTabVisibility';
import { useVoiceStatsOverview } from '@/hooks/useVoiceStats';
import { formatCurrency } from '@/lib/format';
import { cn } from '@/lib/utils';

// ---------------------------------------------------------------------------
// Date-range presets. Anchored to the current wall-clock time and recomputed
// per render so "Last 7 days" tomorrow yields a different window. UTC
// throughout — the backend stores timestamps in UTC and the chart axis
// re-localises to the browser TZ. Default = 7d, which matches the KPI windows
// (delivery rate + period volume) so an untouched dashboard reads coherently.
// ---------------------------------------------------------------------------
type PresetKey = '7d' | '30d' | '90d';

function makePresets(now: Date = new Date()): Record<PresetKey, StatsRange> {
  const dayMs = 24 * 60 * 60 * 1000;
  const toIso = (d: Date) => d.toISOString();
  const startOfDay = (d: Date) =>
    new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate()));
  const today = startOfDay(now);
  return {
    '7d': { from: toIso(new Date(today.getTime() - 7 * dayMs)), to: toIso(now) },
    '30d': { from: toIso(new Date(today.getTime() - 30 * dayMs)), to: toIso(now) },
    '90d': { from: toIso(new Date(today.getTime() - 90 * dayMs)), to: toIso(now) },
  };
}

// (current - previous) / previous is computed server-side; this helper only
// derives the today-vs-yesterday delta from the volume tail.
function tailDelta(counts: number[]): number | null {
  if (counts.length < 2) return null;
  const today = counts[counts.length - 1] ?? 0;
  const yesterday = counts[counts.length - 2] ?? 0;
  if (yesterday === 0) return null;
  return (today - yesterday) / yesterday;
}

// ---------------------------------------------------------------------------
// KPI cards
// ---------------------------------------------------------------------------
interface KpiCardProps {
  label: string;
  value: string;
  suffix?: string;
  hint?: string;
  loading?: boolean;
  sparkData?: number[];
  sparkColor?: string;
  delta?: number | null;
  deltaLabel?: string;
  /** Inverts delta coloring — used where a rising number is bad. */
  deltaInverted?: boolean;
  accent?: 'volt' | 'warn' | 'danger' | 'voice';
}

function DeltaBadge({
  delta,
  deltaLabel,
  deltaInverted,
}: {
  delta: number;
  deltaLabel?: string;
  deltaInverted?: boolean;
}): JSX.Element {
  const positive = delta >= 0;
  const good = deltaInverted ? !positive : positive;
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 text-[12px] font-medium',
        good ? 'text-success' : 'text-danger',
      )}
    >
      {positive ? (
        <Icon.arrowUp width="12" height="12" />
      ) : (
        <Icon.arrowDown width="12" height="12" />
      )}
      <span className="num">
        {positive ? '+' : ''}
        {(delta * 100).toFixed(1)}%
      </span>
      {deltaLabel && <span className="text-text-3 font-normal ml-1">{deltaLabel}</span>}
    </span>
  );
}

function AccentStrip({ accent }: { accent?: KpiCardProps['accent'] }): JSX.Element | null {
  const accentClass =
    accent === 'volt'
      ? 'bg-volt'
      : accent === 'warn'
        ? 'bg-warn'
        : accent === 'danger'
          ? 'bg-danger'
          : accent === 'voice'
            ? 'bg-voice'
            : null;
  if (!accentClass) return null;
  return (
    <span aria-hidden="true" className={cn('absolute top-0 left-0 right-0 h-[2px]', accentClass)} />
  );
}

function KpiCard({
  label,
  value,
  suffix,
  hint,
  loading,
  sparkData,
  sparkColor,
  delta,
  deltaLabel,
  deltaInverted,
  accent,
}: KpiCardProps): JSX.Element {
  return (
    <Card padding="md" className="relative overflow-hidden">
      <AccentStrip accent={accent} />
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">{label}</div>
      {loading ? (
        <Skeleton className="h-8 w-24 mt-2" />
      ) : (
        <div className="flex items-baseline gap-1.5 mt-1.5">
          <span className="num display text-[26px] font-semibold text-text tracking-tight leading-none">
            {value}
          </span>
          {suffix && <span className="text-[13px] text-text-3 font-medium">{suffix}</span>}
        </div>
      )}
      <div className="flex items-end justify-between mt-2 min-h-[36px]">
        {!loading && delta != null && Number.isFinite(delta) ? (
          <DeltaBadge delta={delta} deltaLabel={deltaLabel} deltaInverted={deltaInverted} />
        ) : hint ? (
          <span className="text-[12px] text-text-3">{hint}</span>
        ) : (
          <span />
        )}
        {!loading && sparkData && sparkData.length >= 2 && (
          <Sparkline data={sparkData} color={sparkColor} />
        )}
      </div>
    </Card>
  );
}

// Thresholds → semantic state. Shared by the delivery + answer-rate gauges.
type RateState = 'healthy' | 'watch' | 'low';

function rateState(rate: number, healthy: number, watch: number): RateState {
  if (rate >= healthy) return 'healthy';
  if (rate >= watch) return 'watch';
  return 'low';
}

const STATE_COLOR: Record<RateState, string> = {
  healthy: 'var(--success)',
  watch: 'var(--warn)',
  low: 'var(--danger)',
};

const STATE_BADGE: Record<RateState, string> = {
  healthy: 'bg-success-bg text-success border-success-bd',
  watch: 'bg-warn-bg text-warn border-warn-bd',
  low: 'bg-danger-bg text-danger border-danger-bd',
};

interface GaugeKpiCardProps {
  label: string;
  /** Fraction in [0,1], or null when there's no data. */
  rate: number | null;
  delta?: number | null;
  deltaLabel?: string;
  loading?: boolean;
  healthy: number;
  watch: number;
  stateLabel: (s: RateState) => string;
  accent?: KpiCardProps['accent'];
}

function GaugeKpiCard({
  label,
  rate,
  delta,
  deltaLabel,
  loading,
  healthy,
  watch,
  stateLabel,
  accent,
}: GaugeKpiCardProps): JSX.Element {
  const state = rate != null ? rateState(rate, healthy, watch) : null;
  const pct = rate != null ? (rate * 100).toFixed(1) : '—';
  return (
    <Card padding="md" className="relative overflow-hidden">
      <AccentStrip accent={accent} />
      <div className="flex items-center justify-between gap-2">
        <span className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">
          {label}
        </span>
        {!loading && state && (
          <span
            className={cn(
              'text-[10px] font-semibold uppercase tracking-[0.04em] px-1.5 py-0.5 rounded-pill border',
              STATE_BADGE[state],
            )}
          >
            {stateLabel(state)}
          </span>
        )}
      </div>
      {loading ? (
        <Skeleton className="h-8 w-20 mt-2" />
      ) : (
        <div className="flex items-baseline gap-1 mt-1.5">
          <span className="num display text-[26px] font-semibold text-text tracking-tight leading-none">
            {pct}
          </span>
          {rate != null && <span className="text-[13px] text-text-3 font-medium">%</span>}
        </div>
      )}
      <div className="mt-2.5">
        <Progress
          value={rate != null ? rate * 100 : 0}
          total={100}
          height={6}
          color={state ? STATE_COLOR[state] : 'var(--text-4)'}
        />
      </div>
      <div className="min-h-[20px] mt-2">
        {!loading && delta != null && Number.isFinite(delta) && (
          <DeltaBadge delta={delta} deltaLabel={deltaLabel} />
        )}
      </div>
    </Card>
  );
}

interface BalanceKpiCardProps {
  label: string;
  value: string;
  spendData: number[];
  runwayDays: number | null;
  fallbackHint: string;
  loading?: boolean;
  accent?: KpiCardProps['accent'];
  runwayHint: (days: number) => string;
}

function BalanceKpiCard({
  label,
  value,
  spendData,
  runwayDays,
  fallbackHint,
  loading,
  accent,
  runwayHint,
}: BalanceKpiCardProps): JSX.Element {
  const hint =
    runwayDays != null && Number.isFinite(runwayDays) ? runwayHint(Math.round(runwayDays)) : fallbackHint;
  return (
    <Card padding="md" className="relative overflow-hidden">
      <AccentStrip accent={accent} />
      <div className="text-[11px] font-medium text-text-4 uppercase tracking-[0.06em]">{label}</div>
      {loading ? (
        <Skeleton className="h-8 w-24 mt-2" />
      ) : (
        <div className="num display text-[26px] font-semibold text-text tracking-tight leading-none mt-1.5">
          {value}
        </div>
      )}
      <div className="flex items-end justify-between mt-2 min-h-[36px]">
        <span className="text-[12px] text-text-3">{hint}</span>
        {!loading && spendData.length >= 2 && (
          <Sparkline data={spendData} color="var(--warn)" />
        )}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Charts
// ---------------------------------------------------------------------------
const STATUS_COLORS: Record<string, string> = {
  delivered: 'var(--success)',
  sent: 'var(--info)',
  queued: 'var(--volt-500)',
  pending: 'var(--volt-500)',
  failed: 'var(--danger)',
  rejected: 'var(--danger)',
  expired: 'var(--text-4)',
};

function colorFor(status: string): string {
  return STATUS_COLORS[status] ?? 'var(--text-4)';
}

interface ChartCardProps {
  title: string;
  action?: ReactNode;
  loading?: boolean;
  empty?: boolean;
  emptyLabel?: string;
  children: ReactNode;
}

function ChartCard({
  title,
  action,
  loading,
  empty,
  emptyLabel,
  children,
}: ChartCardProps): JSX.Element {
  return (
    <Card padding="lg" className="min-h-[300px] flex flex-col">
      <div className="flex items-center justify-between gap-3 mb-4">
        <h2 className="display text-[15px] font-semibold text-text">{title}</h2>
        {action}
      </div>
      {loading ? (
        <div className="flex-1 grid place-items-center">
          <Skeleton className="h-[220px] w-full" />
        </div>
      ) : empty ? (
        <div className="flex-1 grid place-items-center text-[13px] text-text-3">{emptyLabel}</div>
      ) : (
        <div className="flex-1 -mx-2">{children}</div>
      )}
    </Card>
  );
}

type Channel = 'both' | 'sms' | 'voice';

interface VolumeRow {
  day: string;
  label: string;
  sms: number;
  voice: number;
}

export default function DashboardPage(): JSX.Element {
  const { t, i18n } = useTranslation('default');
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = (i18n.resolvedLanguage ?? i18n.language ?? 'en').slice(0, 2);
  const firstName = user?.name.split(' ')[0] ?? user?.email ?? '';

  const visible = useTabVisibility();
  const voiceOn = useFeature('voice');
  // Voice-enabled tenants pick SMS vs Voice first; SMS-only tenants go
  // straight to the SMS flow (chooser never mounts) — unchanged behavior.
  const [channelChooserOpen, setChannelChooserOpen] = useState(false);
  const onNewCampaign = () =>
    voiceOn ? setChannelChooserOpen(true) : navigate('/campaigns');

  // ---- Date range --------------------------------------------------------
  const presets = useMemo(() => makePresets(), []);
  const [presetKey, setPresetKey] = useState<string>('7d');
  const [customRange, setCustomRange] = useState<StatsRange>(presets['7d']);
  const range: StatsRange =
    presetKey === 'custom' ? customRange : presets[presetKey as PresetKey];

  const presetLabel: Record<PresetKey, string> = {
    '7d': t('dash_range_7d'),
    '30d': t('dash_range_30d'),
    '90d': t('dash_range_90d'),
  };
  const pickerPresets: DateRangePreset[] = (Object.keys(presets) as PresetKey[]).map((key) => ({
    key,
    label: presetLabel[key],
    range: presets[key],
  }));

  const stats = useStatsOverview({ enabled: visible, range });
  const voiceStats = useVoiceStatsOverview({ enabled: visible && voiceOn });
  // Reuses the cache the Campaigns page already keeps warm, so the running-
  // campaign banner appears instantly when the operator lands here.
  const campaigns = useCampaigns({ enabled: visible });

  const data = stats.data;
  const isLoading = stats.isLoading;
  const vdata = voiceStats.data;
  const voiceLoading = voiceStats.isLoading;

  const [channel, setChannel] = useState<Channel>('both');

  // ---- SMS KPI values ----------------------------------------------------
  const sentTodayLabel = data ? data.sent_today.toLocaleString(lang) : '0';
  const totalWeekLabel = data ? data.total_last_7d.toLocaleString(lang) : '0';
  const balanceLabel = data ? formatCurrency(data.balance, lang) : '—';

  const volumeCounts = useMemo(() => data?.volume.map((v) => v.count) ?? [], [data]);
  const sparkSent = useMemo(() => volumeCounts.slice(-12), [volumeCounts]);
  const sparkWeek = useMemo(() => volumeCounts.slice(-7), [volumeCounts]);
  const sentDelta = useMemo(() => tailDelta(volumeCounts), [volumeCounts]);
  const spendData = useMemo(() => data?.spend_series.map((s) => s.amount) ?? [], [data]);

  // ---- Voice KPI values --------------------------------------------------
  const voiceVolumeCalls = useMemo(() => vdata?.volume.map((v) => v.calls) ?? [], [vdata]);
  const sparkVoiceToday = useMemo(() => voiceVolumeCalls.slice(-12), [voiceVolumeCalls]);
  const sparkVoiceWeek = useMemo(() => voiceVolumeCalls.slice(-7), [voiceVolumeCalls]);

  // ---- Banner state ------------------------------------------------------
  const runningCampaign = useMemo(
    () => campaigns.data?.items.find((c) => c.status === 'running') ?? null,
    [campaigns.data],
  );
  const isLowBalance = !isLoading && data != null && data.balance < data.low_balance_threshold;
  const todayLabel = useMemo(
    () =>
      new Date().toLocaleDateString(lang, {
        weekday: 'long',
        day: 'numeric',
        month: 'short',
      }),
    [lang],
  );

  // ---- Status donut data --------------------------------------------------
  const statusData: Array<StatusBucket & { fill: string }> = useMemo(() => {
    if (!data) return [];
    return data.status_counts.map((s) => ({ ...s, fill: colorFor(s.status) }));
  }, [data]);

  const statusTotal = useMemo(
    () => statusData.reduce((sum, s) => sum + s.count, 0),
    [statusData],
  );

  // Elevated rejected+failed share → inline attention callout under the donut.
  const rejectedShare = useMemo(() => {
    if (statusTotal === 0) return 0;
    const bad = statusData
      .filter((s) => s.status === 'rejected' || s.status === 'failed')
      .reduce((sum, s) => sum + s.count, 0);
    return bad / statusTotal;
  }, [statusData, statusTotal]);
  const showAttention = rejectedShare > 0.02;

  // ---- Volume timeline data ----------------------------------------------
  const volumeData: VolumeRow[] = useMemo(() => {
    if (!data) return [];
    const voiceByDay = new Map<string, number>();
    for (const v of vdata?.volume ?? []) voiceByDay.set(v.day, v.calls);
    return data.volume.map((v) => ({
      day: v.day,
      label: new Date(`${v.day}T00:00:00Z`).toLocaleDateString(lang, {
        day: 'numeric',
        month: 'short',
      }),
      sms: v.count,
      voice: voiceByDay.get(v.day) ?? 0,
    }));
  }, [data, vdata, lang]);

  const showVoiceSeries = voiceOn && (channel === 'both' || channel === 'voice');
  const showSMSSeries = channel === 'both' || channel === 'sms';

  const volumeMax = useMemo(
    () =>
      volumeData.reduce((m, v) => {
        const val =
          (showSMSSeries ? v.sms : 0) + (showVoiceSeries ? v.voice : 0);
        return val > m ? val : m;
      }, 0),
    [volumeData, showSMSSeries, showVoiceSeries],
  );

  const lastRow = volumeData.length > 0 ? volumeData[volumeData.length - 1] : null;

  const statusLabel = (status: string): string => {
    const key = `msg_status_${status}`;
    const translated = t(key);
    return translated === key ? status : translated;
  };

  const stateLabel = (s: RateState): string => t(`dash_gauge_state_${s}`);

  return (
    <div className="px-8 py-8 max-w-[1200px] mx-auto">
      {runningCampaign && (
        <div
          role="status"
          className="flex items-center gap-3 px-4 py-2.5 mb-5 bg-volt-50 border border-volt-200 rounded-md"
        >
          <LiveDot />
          <div className="flex-1 text-[13px]">
            <strong className="font-semibold text-volt-700">
              {t('dash_banner_running_title')}
            </strong>
            <span className="text-text-2 ml-1.5">
              {t('dash_banner_running_desc', { name: runningCampaign.name })}
            </span>
          </div>
          <Button size="sm" variant="secondary" onClick={() => navigate('/campaigns')}>
            {t('dash_banner_running_cta')}
          </Button>
        </div>
      )}

      {isLowBalance && (
        <div
          role="alert"
          className="flex items-center gap-3 px-4 py-2.5 mb-5 bg-warn-bg border border-warn-bd rounded-md"
        >
          <Icon.warn width="18" height="18" className="text-warn flex-shrink-0" />
          <div className="flex-1 text-[13px]">
            <strong className="font-semibold text-warn">
              {t('dash_banner_low_balance_title')}
            </strong>
            <span className="text-text-2 ml-1.5">
              {t('dash_banner_low_balance_desc', {
                balance: formatCurrency(data?.balance ?? 0, lang),
              })}
            </span>
          </div>
          <Button size="sm" onClick={() => navigate('/billing')}>
            {t('dash_banner_low_balance_cta')}
          </Button>
        </div>
      )}

      <header className="flex items-start justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="text-[12px] text-text-3 mb-1.5">
            {t('dash_today')} · {todayLabel}
          </div>
          <h1 className="display text-[28px] font-semibold tracking-tight text-text leading-none m-0">
            {firstName ? `${t('dash_greeting', { name: firstName })} 👋` : t('nav_dashboard')}
          </h1>
        </div>
        <div className="flex flex-col items-end gap-2.5">
          <DateRangePicker
            presets={pickerPresets}
            value={presetKey}
            range={range}
            onChange={({ key, range: r }) => {
              setPresetKey(key);
              if (key === 'custom') setCustomRange(r);
            }}
          />
          <div className="flex gap-2.5">
            <Button variant="secondary" onClick={() => navigate('/contacts')}>
              {t('dash_action_import')}
            </Button>
            <Button onClick={onNewCampaign}>
              <Icon.plus width="14" height="14" />
              {t('dash_action_new_campaign')}
            </Button>
          </div>
        </div>
      </header>

      {stats.isError && (
        <Card padding="md" className="mb-6 border-danger-bd bg-danger-bg">
          <div className="flex items-center justify-between gap-3">
            <p className="text-[13px] text-danger">{t('dash_load_error')}</p>
            <Button
              size="sm"
              variant="secondary"
              onClick={() => void stats.refetch()}
              disabled={stats.isFetching}
            >
              {stats.isFetching ? t('dash_load_retrying') : t('dash_load_retry')}
            </Button>
          </div>
        </Card>
      )}

      {/* SMS KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <KpiCard
          label={t('dash_kpi_sent_today')}
          value={sentTodayLabel}
          loading={isLoading}
          sparkData={sparkSent}
          sparkColor="var(--volt-500)"
          delta={sentDelta}
          deltaLabel={t('dash_delta_vs_yesterday')}
          accent={runningCampaign ? 'volt' : undefined}
        />
        <GaugeKpiCard
          label={t('dash_kpi_delivery_rate')}
          rate={data?.delivery_rate ?? null}
          delta={data?.delivery_rate_delta ?? null}
          deltaLabel={t('dash_delta_vs_prev')}
          loading={isLoading}
          healthy={0.9}
          watch={0.75}
          stateLabel={stateLabel}
        />
        <KpiCard
          label={t('dash_kpi_week_volume')}
          value={totalWeekLabel}
          loading={isLoading}
          sparkData={sparkWeek}
          sparkColor="var(--info)"
          delta={data?.period_volume_delta ?? null}
          deltaLabel={t('dash_delta_vs_prev')}
        />
        <BalanceKpiCard
          label={t('dash_kpi_balance')}
          value={balanceLabel}
          spendData={spendData}
          runwayDays={data?.runway_days ?? null}
          fallbackHint={t('dash_kpi_balance_hint')}
          runwayHint={(days) => t('dash_kpi_balance_runway', { days })}
          loading={isLoading}
          accent={isLowBalance ? 'warn' : undefined}
        />
      </div>

      {/* Voice KPI row — only when the tenant has the voice module enabled. */}
      {voiceOn && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <span className="w-1.5 h-1.5 rounded-pill bg-voice" aria-hidden="true" />
            <h2 className="text-[12px] font-semibold uppercase tracking-[0.08em] text-text-3">
              {t('dash_voice_section')}
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <KpiCard
              label={t('dash_voice_calls_today')}
              value={vdata ? vdata.calls_today.toLocaleString(lang) : '0'}
              loading={voiceLoading}
              sparkData={sparkVoiceToday}
              sparkColor="var(--voice)"
              delta={vdata?.calls_today_delta ?? null}
              deltaLabel={t('dash_delta_vs_yesterday')}
              accent="voice"
            />
            <GaugeKpiCard
              label={t('dash_voice_answer_rate')}
              rate={vdata?.answer_rate ?? null}
              loading={voiceLoading}
              healthy={0.5}
              watch={0.3}
              stateLabel={stateLabel}
              accent="voice"
            />
            <KpiCard
              label={t('dash_voice_transfers')}
              value={vdata ? vdata.transfers_today.toLocaleString(lang) : '0'}
              loading={voiceLoading}
              delta={vdata?.transfers_today_delta ?? null}
              deltaLabel={t('dash_delta_vs_yesterday')}
              accent="voice"
            />
            <KpiCard
              label={t('dash_voice_call_volume')}
              value={vdata ? vdata.call_volume_7d.toLocaleString(lang) : '0'}
              loading={voiceLoading}
              sparkData={sparkVoiceWeek}
              sparkColor="var(--voice)"
              delta={vdata?.call_volume_delta ?? null}
              deltaLabel={t('dash_delta_vs_prev')}
              hint={
                vdata?.transfer_rate_7d != null
                  ? t('dash_voice_transfer_rate', {
                      pct: (vdata.transfer_rate_7d * 100).toFixed(1),
                    })
                  : undefined
              }
              accent="voice"
            />
          </div>
        </>
      )}

      {/* Charts row */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        <div className="lg:col-span-2">
          <ChartCard
            title={t('dash_status_title')}
            loading={isLoading}
            empty={!isLoading && statusData.length === 0}
            emptyLabel={t('dash_no_messages')}
          >
            <div className="relative h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={statusData}
                    dataKey="count"
                    nameKey="status"
                    innerRadius={60}
                    outerRadius={95}
                    paddingAngle={2}
                    stroke="var(--surface)"
                    strokeWidth={2}
                  >
                    {statusData.map((s) => (
                      <Cell key={s.status} fill={s.fill} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      background: 'var(--surface)',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    formatter={(value: number, _: string, props) => [
                      value.toLocaleString(lang),
                      statusLabel(String(props.payload.status)),
                    ]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="absolute inset-0 grid place-items-center pointer-events-none">
                <div className="text-center">
                  <div className="num display text-[22px] font-semibold text-text">
                    {statusTotal.toLocaleString(lang)}
                  </div>
                  <div className="text-[11px] text-text-3 uppercase tracking-[0.06em] mt-0.5">
                    {t('dash_status_total')}
                  </div>
                </div>
              </div>
            </div>
            <ul className="grid grid-cols-2 gap-1.5 mt-3 text-[12px]">
              {statusData.map((s) => (
                <li key={s.status} className="flex items-center gap-2">
                  <span
                    aria-hidden="true"
                    className="w-2.5 h-2.5 rounded-sm flex-shrink-0"
                    style={{ background: s.fill }}
                  />
                  <span className="text-text-2 flex-1 truncate">{statusLabel(s.status)}</span>
                  <span className="num text-text-3">{s.count.toLocaleString(lang)}</span>
                </li>
              ))}
            </ul>
            {showAttention && (
              <div
                role="note"
                className="flex items-start gap-2 mt-3 px-3 py-2 bg-warn-bg border border-warn-bd rounded-md text-[12px] text-warn"
              >
                <Icon.warn width="14" height="14" className="flex-shrink-0 mt-0.5" />
                <span>
                  {t('dash_attention_rejected', { pct: (rejectedShare * 100).toFixed(1) })}
                </span>
              </div>
            )}
          </ChartCard>
        </div>

        <div className="lg:col-span-3">
          <ChartCard
            title={t('dash_volume_title')}
            loading={isLoading}
            empty={!isLoading && volumeMax === 0}
            emptyLabel={t('dash_no_volume')}
            action={
              voiceOn ? (
                <div
                  role="tablist"
                  className="inline-flex bg-surface-2 border border-line rounded-md p-0.5"
                >
                  {(['both', 'sms', 'voice'] as Channel[]).map((c) => (
                    <button
                      key={c}
                      type="button"
                      role="tab"
                      aria-selected={channel === c}
                      onClick={() => setChannel(c)}
                      className={cn(
                        'px-2.5 py-1 text-[11px] font-medium rounded-sm transition-colors',
                        channel === c
                          ? 'bg-surface text-text border border-line'
                          : 'text-text-3 hover:text-text border border-transparent',
                      )}
                    >
                      {t(`dash_channel_${c}`)}
                    </button>
                  ))}
                </div>
              ) : undefined
            }
          >
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={volumeData} margin={{ top: 8, right: 16, bottom: 8, left: 0 }}>
                  <defs>
                    <linearGradient id="smsGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="var(--volt-500)" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="var(--volt-500)" stopOpacity="0" />
                    </linearGradient>
                    <linearGradient id="voiceGrad" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="var(--voice)" stopOpacity="0.28" />
                      <stop offset="100%" stopColor="var(--voice)" stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="var(--line)" strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fill: 'var(--text-3)', fontSize: 11 }}
                    axisLine={{ stroke: 'var(--line)' }}
                    tickLine={false}
                    interval="preserveStartEnd"
                    minTickGap={24}
                  />
                  <YAxis
                    tick={{ fill: 'var(--text-3)', fontSize: 11 }}
                    axisLine={false}
                    tickLine={false}
                    width={36}
                    allowDecimals={false}
                  />
                  <Tooltip
                    contentStyle={{
                      background: 'var(--surface)',
                      border: '1px solid var(--line)',
                      borderRadius: 6,
                      fontSize: 12,
                    }}
                    labelFormatter={(_, payload) => {
                      const day = payload?.[0]?.payload?.day as string | undefined;
                      if (!day) return '';
                      return new Date(`${day}T00:00:00Z`).toLocaleDateString(lang, {
                        weekday: 'short',
                        day: 'numeric',
                        month: 'short',
                      });
                    }}
                    formatter={(value: number, name: string) => [
                      value.toLocaleString(lang),
                      name === 'voice' ? t('dash_channel_voice') : t('dash_volume_label'),
                    ]}
                  />
                  {showSMSSeries && (
                    <Area
                      type="monotone"
                      dataKey="sms"
                      stackId={channel === 'both' ? 'vol' : undefined}
                      stroke="var(--volt-500)"
                      strokeWidth={2}
                      fill="url(#smsGrad)"
                      dot={false}
                      activeDot={{ r: 4, fill: 'var(--volt-500)' }}
                    />
                  )}
                  {showVoiceSeries && (
                    <Area
                      type="monotone"
                      dataKey="voice"
                      stackId={channel === 'both' ? 'vol' : undefined}
                      stroke="var(--voice)"
                      strokeWidth={2}
                      fill="url(#voiceGrad)"
                      dot={false}
                      activeDot={{ r: 4, fill: 'var(--voice)' }}
                    />
                  )}
                  {/* Emphasized last point on the SMS series (single-channel
                      views only — a stacked top point would sit at the
                      combined height and mislead). */}
                  {lastRow && showSMSSeries && channel === 'sms' && (
                    <ReferenceDot
                      x={lastRow.label}
                      y={lastRow.sms}
                      r={4}
                      fill="var(--volt-500)"
                      stroke="var(--surface)"
                      strokeWidth={2}
                      isFront
                    />
                  )}
                  {lastRow && channel === 'voice' && showVoiceSeries && (
                    <ReferenceDot
                      x={lastRow.label}
                      y={lastRow.voice}
                      r={4}
                      fill="var(--voice)"
                      stroke="var(--surface)"
                      strokeWidth={2}
                      isFront
                    />
                  )}
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </ChartCard>
        </div>
      </div>

      {voiceOn && (
        <CampaignChannelChooser
          open={channelChooserOpen}
          onClose={() => setChannelChooserOpen(false)}
          smsTo="/campaigns"
        />
      )}
    </div>
  );
}
